/* eslint-disable react/no-array-index-key */
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Field } from 'formik'
import classNames from 'classnames'
import {
  IconRefresh,
  IconArrowRight,
  IconMinus,
  IconArrowUp,
  IconPlayerStopFilled,
} from '@tabler/icons-react'
import { isEmpty } from 'lodash'
import Markdown from 'marked-react'

import { Button } from '@/components/buttons/Button'
import { CopyButton } from '@/components/buttons/CopyButton'
import {
  searchCallsWithBaltoGPT,
  searchTranscriptWithBaltoGPT,
  resetChatFeed,
} from '@/reducers/baltoGPT/baltoGPT.actions'
import { setLoading } from '@/reducers/baltoGPT/baltoGPT.redux'
import { BaltoGPTIcon } from '@/components/icons/BaltoGPTIcon'
import { CogitationOrb } from '@/components/ai/CogitationOrb'
import { markdownLinkRenderer } from '@/utils/helpers'
import { CHANNEL_NAME, CHANNEL_NAME_PLURAL, COGITATION_TOKENS } from '@/utils/constants'

import { getBaltoGPTSuggestionsForChannel, linkifyCallIds } from './baltoGPT.helpers'

import './BaltoGPTChat.scss'
import { PoweredByAI } from '../ai/PoweredByAI'

const TypewriterMarkdown = ({ text, renderer, onComplete, scrollFn }) => {
  const [displayedText, setDisplayedText] = useState('')
  const indexRef = useRef(0)

  useEffect(() => {
    const charsPerTick = Math.max(1, Math.ceil(Math.sqrt(text.length) / 8))
    indexRef.current = 0
    setDisplayedText('')

    const interval = setInterval(() => {
      indexRef.current = Math.min(indexRef.current + charsPerTick, text.length)
      setDisplayedText(text.slice(0, indexRef.current))
      if (scrollFn) scrollFn()

      if (indexRef.current >= text.length) {
        clearInterval(interval)
        if (onComplete) onComplete()
      }
    }, 16)

    return () => clearInterval(interval)
  }, [text])

  return <Markdown renderer={renderer}>{displayedText}</Markdown>
}

export const BaltoGPTChat = ({
  handleToggleChatVisible,
  isDetailPage,
  callIds,
  channel,
  useMinimalUi = false,
  onSubmit,
  suggestions: customSuggestions,
  disableAutoScroll = false,
  disableAutoFocus = false,
}) => {
  const dispatch = useDispatch()
  const {
    loading: { baltoGPTChat: baltoGPTChatLoading },
    chatFeed,
  } = useSelector((state) => state.baltoGPT)
  const [abortController, setAbortController] = useState(null)
  const [cogitationToken, setCogitationToken] = useState('')
  const chatFeedRef = useRef(null)
  const chatEndRef = useRef(null)
  const textAreaRef = useRef(null)
  const [typingCompleteIndex, setTypingCompleteIndex] = useState(-1)
  const initialValues = isDetailPage ? { prompt: '' } : { summary_section: 'reason', prompt: '' }
  const suggestions = getBaltoGPTSuggestionsForChannel(channel)

  const handleUpdatePrompWithSuggestion = (suggestion, setFieldValue) => {
    if (suggestion.summarySection) {
      setFieldValue('summary_section', suggestion.summarySection)
    }
    setFieldValue('prompt', suggestion.text)
  }

  const handleSubmit = (values, { setFieldValue }) => {
    if (!values.prompt?.trim()) {
      return
    }

    const newController = new AbortController()
    setAbortController(newController)

    if (onSubmit) {
      onSubmit(values, newController.signal)
    } else if (isDetailPage) {
      dispatch(searchTranscriptWithBaltoGPT(values))
    } else {
      const payload = { ...values, call_ids: callIds }
      dispatch(searchCallsWithBaltoGPT(payload))
    }

    setFieldValue('prompt', '')
    textAreaRef.current?.focus()
  }

  const handleAbort = () => {
    if (abortController) {
      abortController.abort()
      setAbortController(null)
    }
    dispatch(setLoading({ baltoGPTChat: false }))
  }

  const scrollToBottom = () => {
    if (disableAutoScroll) {
      return
    }
    chatEndRef?.current?.scrollIntoView()
  }

  const handleKeyUp = (event, handleSubmit, values, setFieldValue) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit(values, { setFieldValue })
    }
  }
  const handleRefresh = () => {
    dispatch(resetChatFeed())
  }

  useEffect(() => {
    if (baltoGPTChatLoading) {
      setCogitationToken(COGITATION_TOKENS[Math.floor(Math.random() * COGITATION_TOKENS.length)])
    }
  }, [baltoGPTChatLoading])

  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort()
      }
    }
  }, [abortController])

  useEffect(() => {
    if (chatFeed.length === 0) {
      setTypingCompleteIndex(-1)
    } else {
      scrollToBottom()
    }
  }, [chatFeed.length])

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ handleSubmit, setFieldValue, values }) => {
        useEffect(() => {
          if (textAreaRef.current) {
            textAreaRef.current.style.height = 'auto'
            textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`
          }
        }, [textAreaRef.current, values.prompt])

        useEffect(() => {
          if (!disableAutoFocus) {
            textAreaRef.current?.focus({ preventScroll: true })
          }
        }, [])

        return !useMinimalUi ? (
          <div className="balto-gpt-chat" data-testid="balto-gpt-chat">
            <header>
              <div className="brand">
                <div className="brand-circle">
                  <BaltoGPTIcon className="icon-svg" />
                </div>
                <div className="brand-name">BaltoGPT</div>
              </div>
              <div className="flex-align-center small-gap">
                <Button
                  square
                  small
                  icon={<IconRefresh />}
                  onClick={handleRefresh}
                  tooltip="Restart chat"
                  aria-label="Restart chat"
                  data-testid="balto-gpt-refresh-button"
                />
                <Button
                  square
                  small
                  icon={<IconMinus />}
                  onClick={handleToggleChatVisible}
                  tooltip="Minimize chat"
                  aria-label="Minimize chat"
                  data-testid="balto-gpt-minimize-button"
                />
              </div>
            </header>
            <section>
              <div className="balto-gpt-chat-box">
                {chatFeed.map((chat, index) => {
                  const isLastItem = chatFeed.length - 1 === index

                  return (
                    <div
                      ref={isLastItem ? chatFeedRef : null}
                      key={`${chat.text}${index}`}
                      className={classNames('chat-item', {
                        'error-chat-bubble': chat.chatType === 'error',
                        'balto-gpt-chat-bubble': chat.chatType === 'baltoGPT',
                        'agent-chat-bubble': chat.chatType === 'agent',
                      })}
                    >
                      {chat.chatType === 'baltoGPT' ? (
                        <div className="chat-content">
                          <Markdown renderer={markdownLinkRenderer}>{chat.text}</Markdown>
                          <CopyButton content={chat.text} iconOnly />
                        </div>
                      ) : (
                        chat.text
                      )}
                    </div>
                  )
                })}
                {!isDetailPage && isEmpty(chatFeed) && (
                  <div className="balto-gpt-suggestions" data-testid="balto-gpt-suggestions">
                    <h3>Suggestions</h3>
                    {suggestions.map((suggestion) => (
                      <button
                        type="button"
                        key={suggestion.text}
                        className="suggestion-bubble"
                        onClick={() => handleUpdatePrompWithSuggestion(suggestion, setFieldValue)}
                      >
                        {suggestion.text}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </section>
            <div className="flex-center">
              <PoweredByAI margin={false} />
            </div>
            <footer>
              <form className="horizontal" onSubmit={handleSubmit}>
                <div className="balto-gpt-footer-container">
                  <Field
                    name="prompt"
                    as="textarea"
                    data-testid="textArea-prompt"
                    rows="1"
                    placeholder={
                      isDetailPage
                        ? `Ask questions about your ${CHANNEL_NAME[channel].toLowerCase()}...`
                        : `Ask questions about your ${CHANNEL_NAME_PLURAL[channel].toLowerCase()}...`
                    }
                    type="text"
                    className="full-width"
                    onKeyUp={(event) => handleKeyUp(event, handleSubmit, values, setFieldValue)}
                    innerRef={textAreaRef}
                  />
                  <Button
                    brand
                    square
                    icon={<IconArrowRight />}
                    data-testid="balto-gpt-submit-button"
                    type="submit"
                    onClick={handleSubmit}
                    loading={baltoGPTChatLoading}
                  />
                </div>
              </form>
            </footer>
          </div>
        ) : (
          <div className="balto-gpt-chat-minimal" data-testid="balto-gpt-chat-minimal">
            <section>
              <div className="balto-gpt-chat-box">
                {chatFeed.map((chat, index) => {
                  const isLastItem = chatFeed.length - 1 === index
                  return (
                    <div
                      ref={isLastItem ? chatFeedRef : null}
                      key={`${chat.text}${index}`}
                      className={classNames('chat-item mb', {
                        'error-chat-bubble': chat.chatType === 'error',
                        'balto-gpt-chat-bubble': chat.chatType === 'baltoGPT',
                        'agent-chat-bubble': chat.chatType === 'agent',
                        'agent-chat-bubble--short':
                          chat.chatType === 'agent' && chat.text.length < 60,
                      })}
                    >
                      {chat.chatType === 'baltoGPT' ? (
                        <div className="chat-content">
                          <div className="flex-align-center">
                            <CogitationOrb resting />
                            <div className="ml balto-gpt-label">BaltoGPT</div>
                          </div>
                          {isLastItem && index > typingCompleteIndex ? (
                            <TypewriterMarkdown
                              text={linkifyCallIds(chat.text)}
                              renderer={markdownLinkRenderer}
                              scrollFn={scrollToBottom}
                              onComplete={() => {
                                setTypingCompleteIndex(index)
                                scrollToBottom()
                              }}
                            />
                          ) : (
                            <Markdown renderer={markdownLinkRenderer}>
                              {linkifyCallIds(chat.text)}
                            </Markdown>
                          )}
                        </div>
                      ) : (
                        chat.text
                      )}
                    </div>
                  )
                })}
                {baltoGPTChatLoading && (
                  <div className="balto-gpt-thinking" data-testid="balto-gpt-thinking">
                    <div className="flex-align-center">
                      <CogitationOrb />
                      <div className="ml balto-gpt-label">BaltoGPT</div>
                    </div>
                    <div className="thinking-text">{cogitationToken}...</div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            </section>
            <footer>
              {!isEmpty(customSuggestions) && isEmpty(chatFeed) && (
                <div className="balto-gpt-suggestions" data-testid="balto-gpt-suggestions">
                  {customSuggestions.map((suggestion) => (
                    <button
                      type="button"
                      key={suggestion.text}
                      className="suggestion-bubble status-pill"
                      onClick={() => {
                        setFieldValue('prompt', suggestion.text)
                        handleSubmit()
                      }}
                    >
                      {suggestion.text}
                    </button>
                  ))}
                </div>
              )}

              <form className="horizontal" onSubmit={handleSubmit}>
                <div
                  className="balto-gpt-footer-container"
                  onClick={(event) => {
                    if (event.target === event.currentTarget) {
                      textAreaRef.current?.focus()
                    }
                  }}
                >
                  <Field
                    name="prompt"
                    as="textarea"
                    data-testid="textArea-prompt"
                    rows="1"
                    placeholder="Ask Balto anything..."
                    type="text"
                    className="full-width"
                    onKeyUp={(event) => handleKeyUp(event, handleSubmit, values, setFieldValue)}
                    innerRef={textAreaRef}
                  />
                  {baltoGPTChatLoading ? (
                    <Button
                      small
                      square
                      icon={<IconPlayerStopFilled className="icon-svg" />}
                      data-testid="balto-gpt-stop-button"
                      className="balto-gpt-submit-brand"
                      onClick={handleAbort}
                    />
                  ) : (
                    <Button
                      small
                      brand
                      square
                      icon={<IconArrowUp className="icon-svg" />}
                      data-testid="balto-gpt-submit-button"
                      className="balto-gpt-submit-brand"
                      type="submit"
                      onClick={handleSubmit}
                    />
                  )}
                </div>
              </form>
              <PoweredByAI className="balto-gpt-disclaimer" />
            </footer>
          </div>
        )
      }}
    </Formik>
  )
}
